// A ASSINATURA Maestra PRO, em palavras.
//
// Irmão do `checkout.ts`, que faz o mesmo para o pagamento único, e existe pela mesma razão: as
// duas superfícies falam do MESMO produto. Uma segunda lista de benefícios escrita no app seria
// uma segunda promessa sobre a mesma assinatura, e elas divergiriam no primeiro ajuste.
//
// ⚠️ SÓ TEXTO AQUI. Os ícones ficam de fora de propósito: o núcleo não conhece DOM nem React
// Native, e cada superfície desenha o seu a partir da `chave`. Uma lista de textos atravessa as
// duas; um `<FiTarget />` não atravessa nenhuma.

/** A chamada do topo — a mesma manchete nas duas telas de planos. */
// ⚠️ "TODOS OS SEUS PERFIS" SAIU, e não por estilo: o PRO inclui UM perfil, e cada perfil a mais
// é um add-on mensal. A frase antiga era a manchete da tela de planos prometendo perfis
// ilimitados logo acima do campo que cobra por perfil adicional.
export const CHAMADA_DO_PRO = {
  titulo: 'Faça mais com o Maestra PRO',
  apoio: 'O planejamento completo, os módulos e a Nyta Assistente no seu perfil de artista.',
} as const;

export type ChaveDoBeneficio = 'executar' | 'nyta' | 'gestao' | 'acompanhar';

export interface GrupoDeBeneficios {
  chave: ChaveDoBeneficio;
  titulo: string;
  itens: readonly string[];
}

export const BENEFICIOS_DO_PRO: readonly GrupoDeBeneficios[] = [
  {
    chave: 'executar',
    titulo: 'Execute o seu plano',
    // "Edição em todos os perfis que você acessa" saiu: quem colabora num perfil de outra pessoa
    // depende do PRO do DONO daquele perfil (`owner_is_pro`), não do próprio. Um colaborador
    // assinante lia a promessa e continuava sem editar o perfil do colega.
    itens: ['Planejamento estratégico completo com a Nyta', 'Crie e gerencie ações e estratégias do plano'],
  },
  {
    chave: 'nyta',
    titulo: 'Assistente de IA ao seu lado',
    // ⚠️ 100/dia por perfil, e NÃO "ilimitado": o limite vem de `nyta_plan_limits` (pro = 100) e o
    // contador é por (usuário, artista, dia UTC) em `nyta_daily_usage`. O próprio chat mostra
    // "X/100" no cabeçalho — prometer ilimitado aqui contradiria a tela seguinte.
    itens: [
      'Nyta Assistente, até 100 interações por dia em cada perfil',
      'Recomendações sob o contexto da sua carreira',
    ],
  },
  {
    chave: 'gestao',
    titulo: 'Gestão completa',
    // ⚠️ NEM "ILIMITADAS", NEM "TODOS OS PERFIS" — as duas promessas eram falsas por motivos
    // diferentes, e as duas apareciam lado a lado.
    //
    // O número de FAIXAS é de fato ilimitado (`maxCatalogTracks` vira Infinity no PRO), mas os
    // ARQUIVOS consomem os 30 GB da conta e o gatilho do Storage recusa o envio acima da cota.
    // Quem lia "ilimitadas" entendia "cabe a minha discografia inteira" e batia num erro.
    //
    // Perfis: o PRO inclui UM; os demais são add-on de assinatura. Ver `CHAMADA_DO_PRO`.
    itens: [
      'Músicas, agenda e equipe, sem limite de cadastros',
      '30 GB para os arquivos da conta, com espaço extra sob demanda',
      'Um perfil de artista incluso, e mais perfis quando precisar',
    ],
  },
] as const;

/**
 * O que a assinatura abre ALÉM dos três pilares do método.
 *
 * A tela de desbloqueio mostra seis cartões: os três da jornada (que vêm de `pilaresDoPainel`,
 * com dado real de quem está olhando) e estes três, que não são etapa de jornada — são o que vem
 * junto. A distinção é o que faz a narrativa funcionar: a jornada tem ORDEM e diz "você concluiu
 * a primeira"; estes aqui não têm ordem nenhuma, e listá-los numerados junto com os pilares
 * achataria as duas coisas numa lista só.
 *
 * Texto no núcleo pelo mesmo motivo do resto do arquivo: o app nativo vai desenhar os mesmos
 * cartões, e duas listas divergiriam no primeiro ajuste.
 */
export interface CartaoDeInclusao {
  chave: 'nyta' | 'modulos' | 'armazenamento';
  titulo: string;
  linha: string;
}

export const INCLUSOES_DO_PRO: readonly CartaoDeInclusao[] = [
  {
    chave: 'nyta',
    titulo: 'Nyta, sua assistente',
    // O limite real é 100/dia por perfil (`nyta_plan_limits`), e o chat mostra "X/100" no
    // cabeçalho. Prometer "ilimitada" aqui contradiria a tela seguinte.
    linha: 'Até 100 conversas por dia em cada perfil, com o contexto da sua carreira.',
  },
  {
    chave: 'modulos',
    titulo: 'Músicas, agenda e equipe',
    // "Sem limite de faixas" era verdade e mentira ao mesmo tempo: o catálogo não conta faixas,
    // mas os arquivos ocupam a cota. O cartão do armazenamento, logo abaixo, é que fecha a frase.
    linha: 'Cadastre quantas faixas quiser, monte o cronograma da carreira e convide o seu time.',
  },
  {
    chave: 'armazenamento',
    titulo: '30 GB de armazenamento',
    linha: 'Para os áudios, capas e o material de divulgação da conta. Precisou de mais, é só somar espaço.',
  },
] as const;

/**
 * O que o plano grátis REALMENTE dá hoje.
 *
 * ⚠️ O texto anterior descrevia o modelo antigo, em que "grátis" significava um perfil JÁ PAGO
 * pelo desbloqueio avulso, sem a assinatura PRO: por isso prometia ver o plano de ação e
 * visualizar músicas, agenda e equipe. Com a cobrança unificada, "grátis" passou a ser quem não
 * pagou nada — e esses módulos abrem bloqueados para ele.
 *
 * A página de preços prometendo o que o app não entrega é o pior lugar possível para uma mentira.
 */
export const BENEFICIOS_DO_GRATIS: readonly GrupoDeBeneficios[] = [
  {
    chave: 'acompanhar',
    titulo: 'Conheça sua carreira',
    itens: [
      'Diagnóstico REAL completo, com as quatro dimensões',
      'Seu perfil de carreira e o que ele revela',
      'Um perfil de artista, para começar',
    ],
  },
] as const;

/** Os dois ciclos, como aparecem na lista de planos. O preço vem do servidor, nunca daqui. */
export const CICLOS_DO_PRO = [
  {
    chave: 'monthly',
    nome: 'Mensal',
    itens: ['Planejamento e módulos liberados', 'Nyta Assistente no seu perfil', 'Cancele quando quiser'],
  },
  {
    chave: 'annual',
    nome: 'Anual',
    itens: ['Tudo o que o plano mensal tem', 'O ano inteiro, com desconto', 'Cancele quando quiser'],
  },
] as const;

/**
 * Como se paga a assinatura.
 *
 * ⚠️ "RECORRENTE" É O QUE DÁ PARA GARANTIR, e o texto para aí de propósito. A assinatura por PIX
 * tem DOIS caminhos e quem decide é o servidor: com `plan.pixAutomaticEnabled` ligado, a pessoa
 * autoriza uma vez e os débitos seguintes são automáticos; desligado (ou se a autorização
 * falhar), o checkout usa o fluxo de cobrança por ciclo, que é um QR novo a cada ciclo.
 *
 * Em 23/09/2026 a chave foi DESLIGADA, e de propósito: a autorização de Pix Automático vale para
 * um valor e não há como alterá-la, o que prendia o assinante ao plano que contratou. Hoje o
 * caminho normal é o QR por ciclo. Este texto não mudou porque foi escrito para ser verdade nos
 * dois estados — prometer "débito automático" quebraria agora, e prometer "um QR por mês"
 * quebraria no dia em que a chave voltasse. Recorrente é verdade sempre.
 */
export const COMO_SE_PAGA = 'Cartão de crédito ou PIX recorrente.';

/**
 * ONDE SE ASSINA — o texto da folha do app.
 *
 * ⚠️ O ENDEREÇO É PARA SER LIDO E DIGITADO, não tocado. A App Store 3.1.3 (anti-steering) alcança
 * botão e link para outro meio de pagamento; um endereço escrito não é nem um nem outro. É por
 * isto que a rota da web deixou de ser `/assinatura` e passou a `/planos`: para caber numa frase
 * que alguém consegue copiar para a barra do navegador.
 *
 * Quem transformar isto num `Pressable` com `Linking.openURL` desfaz a razão de a tela existir —
 * e há um caso em `portaDaCobranca.test.ts` que apanha isso.
 */
export const ONDE_SE_ASSINA = {
  endereco: 'www.maestramanager.com/planos',
  titulo: 'Para assinar, acesse www.maestramanager.com/planos',
  corpo: 'Não é possível assinar pelo app.',
  entendi: 'Entendi',
} as const;
